import {
	Select,
	MenuItem,
	Stack,
	Pagination,
	Box,
	Button,
	IconButton,
	PaginationItem,
	TextField,
	Typography,
	CircularProgress,
	useMediaQuery,
	useTheme,
  } from '@mui/material';

import FirstPageIcon from '@mui/icons-material/FirstPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
  import { useMemo, useState } from 'react';
  
const prevNextButtonSx = {
	color: '#000 !important',
	textTransform: 'none',
	minWidth: 'auto',
	'& .MuiSvgIcon-root': {
		color: '#000 !important',
	},
	'& .MuiButton-startIcon': {
		marginRight: '-4px',
	},
	'& .MuiButton-endIcon': {
		marginLeft: '-4px',
	},
	'&:hover': {
		backgroundColor: 'rgba(90, 84, 152, 0.2)',
	},
	'&.Mui-disabled': {
		color: 'rgba(0, 0, 0, 0.26) !important',
	},
	'&.Mui-disabled .MuiSvgIcon-root': {
		color: 'rgba(0, 0, 0, 0.26) !important',
	},
};

  export function PaginationDetails({
	page,
	pageSize,
	paginationData,
	listLength,
	handlepageSizeChange,
	handlePageChange,
	showGoToPage = false,
	pageSizeOptions,
	isPrevNextPagination = false,
	isTotalCountLoading = false,
  }: any) {
	const [inputPage, setInputPage] = useState('');
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const menuItemSx = {
			padding: '0px',
			backgroundColor: `${theme.palette.dropdown.menuItemBackground} !important`,
			color: `${theme.palette.dropdown.menuItemText} !important`,
			'&.Mui-selected': {
				backgroundColor: `${theme.palette.dropdown.menuItemSelected} !important`,
				color: `${theme.palette.dropdown.menuItemHoverText} !important`,
			},
			'&.Mui-selected:hover': {
				backgroundColor: `${theme.palette.dropdown.menuItemHover} !important`,
				color: `${theme.palette.dropdown.menuItemHoverText} !important`,
			},
			'&:hover': {
				backgroundColor: `${theme.palette.dropdown.menuItemHover} !important`,
				color: `${theme.palette.dropdown.menuItemHoverText} !important`,
			},
		};
  
	const handleGoToPage = () => {
	  const pageNumber = Number(inputPage);
	  if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= paginationData.totalPages) {
		handlePageChange(null, pageNumber);
	  } else {
		alert(`Please enter a valid page number between 1 and ${paginationData.totalPages}`);
	  }
	};
  
	const validate = useMemo(() => {
	  const pageNumber = Number(inputPage?.trim());
	  return (
		Number.isInteger(pageNumber) &&
		pageNumber >= 1 &&
		pageNumber <= paginationData.totalPages &&
		pageNumber !== page
	  );
	},[inputPage, paginationData.totalPages, page]);

	const totalElements = listLength ? Number(paginationData?.totalElements || 0) : 0;
	const parsedPageSize = parseInt(pageSize) || 10;
	const startRecord = totalElements === 0 ? 0 : page * parsedPageSize - parsedPageSize + 1;
	const endRecord =
		totalElements === 0
			? 0
			: page * parsedPageSize > totalElements
			? totalElements
			: page * parsedPageSize;

	return (
	  <Box
		sx={{
		  display: 'flex',
		  flexDirection: { xs: 'column', sm: 'row' },
		  justifyContent: { xs: 'center', sm: 'space-between' },
		  alignItems: 'center',
		  gap: 2,
		  px: 1,
		  marginTop: 1,
		  borderRadius: "4px",
		  backgroundColor: `${theme.palette.table.pagination.BgColor} !important`,
		}}
	  >
		{/* Summary Text */}
		<Typography
		  variant="bodyText"
		  color={`${theme.palette.table.pagination.text} !important`}
		  sx={{ whiteSpace: 'nowrap' }}
		>
		  Showing {startRecord.toLocaleString()} - {endRecord.toLocaleString()} of{' '}
		  {isTotalCountLoading ? (
			<CircularProgress size={14} sx={{ verticalAlign: 'middle', mx: 0.5 }} />
		  ) : (
			totalElements.toLocaleString()
		  )}{' '}
		  results
		</Typography>
  
		{/* Right Section */}
		<Stack
		  direction={{ xs: 'column', sm: 'row' }}
		  spacing={2}
		  alignItems={{ xs: 'center' }}
		  justifyContent= {{ xs: 'center', sm: 'flex-end' }}
		  width="100%"
		>
		  {/* Rows per page selector */}
		  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
			<Typography variant="bodyText" color={`${theme.palette.table.pagination.text} !important`} sx={{  whiteSpace: 'nowrap' }}>
			  Rows per Page:
			</Typography>
				<Select
					sx={{
						color: `${theme.palette.table.pagination.pageSizeText} !important`,
						"&& .MuiSelect-select.MuiSelect-select": {
							...theme.typography.bodyText,
							color: `${theme.palette.table.pagination.pageSizeText} !important`,
						},
						'& .MuiSvgIcon-root': {
							color: `${theme.palette.DropdownV2.dropdownIconDefault} !important`
						},
					}}
					MenuProps={{
						PaperProps: {
							sx: {
								backgroundColor: `${theme.palette.DropdownV2.menuItemBackground} !important`,
								color: `${theme.palette.DropdownV2.menuItemText} !important`,
								borderRadius: '4px',
								'& .MuiMenuItem-root': {
									backgroundColor: 'transparent !important',
									color: `${theme.palette.DropdownV2.menuItemText} !important`
								},
								'& .MuiMenuItem-root.Mui-selected': {
									backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
									color: `${theme.palette.DropdownV2.menuItemSelectedText} !important`
								},
								'& .MuiMenuItem-root.Mui-selected:hover': {
									backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
									color: `${theme.palette.DropdownV2.menuItemSelectedText} !important`
								},
								'& .MuiMenuItem-root:hover': {
									backgroundColor: `${theme.palette.DropdownV2.menuItemHover} !important`,
									color: `${theme.palette.DropdownV2.menuItemHoverText} !important`
								}
							},
						},
						MenuListProps: {
							sx: {
								paddingTop: 0,
								paddingBottom: 0,
								backgroundColor: `${theme.palette.DropdownV2.menuItemBackground} !important`
							},
						},
					}}
					labelId="pageSize"
					id="pageSize"
					value={pageSize}
					onChange={handlepageSizeChange}
				>
					{!(pageSizeOptions ? pageSizeOptions : [100, 200, 500, 1000])
						.map(String)
						.includes(String(pageSize)) && (
							<MenuItem value={String(pageSize)}>{pageSize}</MenuItem>
						)}
					{(pageSizeOptions
						? pageSizeOptions
						: [100, 200, 500, 1000]
					).map((size: number) => (
						<MenuItem key={size} value={String(size)}>{size}</MenuItem>
					))}
				</Select>
				</Stack>
				{isPrevNextPagination ? (
					<Stack direction="row" alignItems="center" spacing={1}>
						<IconButton
							aria-label="First page"
							disabled={Number(page) <= 1}
							onClick={(event) => handlePageChange(event, 1)}
							sx={prevNextButtonSx}
						>
							<FirstPageIcon />
						</IconButton>
						<Button
							variant="text"
							startIcon={<ChevronLeftIcon />}
							disabled={Number(page) <= 1}
							onClick={(event) => handlePageChange(event, Number(page) - 1)}
							sx={prevNextButtonSx}
						>
							Prev
						</Button>
						<Button
							variant="text"
							endIcon={<ChevronRightIcon />}
							disabled={Number(page) >= paginationData.totalPages}
							onClick={(event) => handlePageChange(event, Number(page) + 1)}
							sx={prevNextButtonSx}
						>
							Next
						</Button>
					</Stack>
				) : (
				<Pagination
					count={paginationData.totalPages}
					page={Number(page)}
					onChange={handlePageChange}
					shape="rounded"
					showFirstButton
					showLastButton
					size={isMobile ? 'small' : 'medium'}
					renderItem={(item) => (
						<PaginationItem 
							{...item} 
							page={item.page?.toLocaleString()}
							sx={{
								color: `${theme.palette.table.pagination.text} !important`, // page number & arrow color
								"&.Mui-selected": {
								...theme.typography.bodyText,
								backgroundColor: `${theme.palette.table.pagination.selectedBackground} !important`,
								color: `${theme.palette.table.pagination.selectedText} !important`,
								},
								"&.MuiPaginationItem-previousNext, &.MuiPaginationItem-firstLast": {
								color: `${theme.palette.table.pagination.arrow} !important`, // arrow color
								},
								"&.MuiPaginationItem-previousNext.Mui-selected, &.MuiPaginationItem-firstLast.Mui-selected": {
								backgroundColor: `${theme.palette.table.pagination.selectedBackground} !important`, // selected arrow bg
								},
							}}
						/>
					)}
					sx={{
						"& .MuiPaginationItem-root": {
						color: `${theme.palette.table.pagination.text} !important`,
						fontSize: "12px",
						},
						"& .MuiPaginationItem-root:hover": {
						backgroundColor: `${theme.palette.table.pagination.hoverBackground} !important`,
						},
						"& .Mui-selected": {
						backgroundColor: `${theme.palette.table.pagination.selectedBackground} !important`,
						color: `${theme.palette.table.pagination.selectedText} !important`,
						},
						"& .MuiPaginationItem-previousNext, & .MuiPaginationItem-firstLast": {
						color: `${theme.palette.table.pagination.arrow} !important`,
						},
					}}
				/>
				)}
				{showGoToPage && (
					<Stack direction="row" alignItems="center" gap={2}>
						<Typography variant="bodyText" color={`${theme.palette.table.pagination.text} !important`}>
							Enter Page Number :{' '}
						</Typography>
						<TextField
							size="small"
							variant="filled"
							value={inputPage}
							onChange={(e) => setInputPage(e.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter') {
									handleGoToPage();
								}
							}}
							sx={{
								width: 40,
								height: 25,
				  input: { color: `${theme.palette.table.pagination.text} !important`, textAlign: 'center', padding: '0px !important' },
					'& .MuiOutlinedInput-root': {
						height: 30,
						fontSize: '0.875rem'
					},
				  '& .MuiFilledInput-root': {
					borderBottom: `1px solid ${theme.palette.table.pagination.text} !important`,
					'&:hover': {
					  borderBottom: '2px solid rgb(0, 155, 216) !important'
					},
					'&.Mui-focused': {
					  borderBottom: '2px solid rgb(0, 155, 216) !important'
					}
				  },
				display: 'flex',
				direction: 'column',
				alignSelf: 'center'
				}}
			  />
			  <Button
				variant="contained"
				onClick={handleGoToPage}
				disabled={!validate}
				sx={{
				  color: 'white',
				  backgroundColor: 'rgb(0, 155, 216)',
				  width: 30,
					height: 30,
				  minWidth: 30,
				  borderRadius: '50%',
				padding: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center'
				}}
			  >
				Go
			  </Button>
			</Stack>
		  )}
		</Stack>
	  </Box>
	);
  }
  